/**
 * Votre trajectoire sur cinq ans : micro, société, holding, et à quel moment
 * passer de l'une à l'autre.
 *
 * LES AUTRES OUTILS RÉPONDENT POUR UNE ANNÉE, CELUI-CI POUR CINQ. Le
 * comparateur de régimes dit lequel laisse le plus à un chiffre d'affaires
 * donné ; « Quand quitter la micro » cherche le chiffre d'affaires de bascule.
 * Un solopreneur qui grandit veut savoir autre chose : en quelle année
 * changer, ce que ça lui rapporte cette année-là, et ce qui l'attend ensuite.
 * On fait donc grandir son chiffre d'affaires au rythme qu'il indique, sa part
 * de dépenses restant la même, et l'on rejoue le comparateur chaque année.
 *
 * UN CHANGEMENT COÛTE, ALORS IL FAUT QU'IL RAPPORTE. Le parcours ne quitte le
 * régime de l'année précédente que si un autre laisse au moins mille euros de
 * plus, le seuil de toute la rubrique : sous ce montant, l'écart est plus
 * petit que la comptabilité d'une société. Et l'on ne revient jamais en
 * arrière : une société ne redevient pas une micro-entreprise parce qu'une
 * année serait un peu meilleure.
 *
 * TROIS ÉCHÉANCES QUE LE COMPARATEUR NE VOIT PAS, parce qu'elles tiennent au
 * temps :
 *   - la micro se ferme l'année qui suit deux années de suite au-dessus du
 *     plafond (microEnterprise.js, statutMicro) ;
 *   - la TVA se facture dès que le chiffre d'affaires passe la franchise, quel
 *     que soit le régime ;
 *   - la holding devient une question le jour où la société gagne plus que ce
 *     que vous voulez vous verser pour vivre : le surplus peut remonter à une
 *     holding au lieu de passer par votre compte (holding.js).
 *
 * LES CALCULS SONT CEUX DES OUTILS EXISTANTS, sans une règle de plus : mêmes
 * barèmes 2026, même société qui verse tout en rémunération dans le
 * comparatif. npm run check:trajectory vérifie qu'ils ne divergent pas.
 */

import { ECART_NEGLIGEABLE, comparerRegimes } from "./businessStructure.js";
import { PLAFONDS, SEUILS_TVA } from "./microEnterprise.js";
import { coutPourNet } from "./monthlyPayout.js";
import { reinvestir } from "./holding.js";

/** Le nombre d'années que l'outil déroule. */
export const ANNEES = 5;

/** Les régimes, du plus simple au plus lourd : on ne redescend jamais. */
export const ORDRE_REGIMES = ["micro", "reel", "eurl", "sasu"];
const SOCIETES = ["eurl", "sasu"];

const positif = (v) => Math.max(0, v);

/** La franchise de TVA de base qui s'applique à l'activité. */
export function franchiseTva(activite) {
    return SEUILS_TVA[activite === "vente" ? "global" : "services"].base;
}

/**
 * Ce que la société peut garder quand vous ne vous versez que ce dont vous
 * avez besoin, et ce qu'une holding en ferait.
 *
 * @param besoin la rémunération nette annuelle, avant impôt sur le revenu,
 *               dont vous voulez vivre
 */
export function surplusSociete({ forme, chiffreAffaires, charges, besoin, activite }) {
    const disponible = positif(chiffreAffaires - charges);
    const cout = coutPourNet({ forme, net: positif(besoin), activite }).cout;
    const surplus = positif(disponible - cout);
    if (surplus < 1) return { disponible, cout, surplus: 0, holding: null };
    const h = reinvestir({
        forme,
        benefice: surplus,
        enveloppeRemuneration: forme === "eurl" ? cout : 0,
        activite,
    });
    return {
        disponible,
        cout,
        surplus,
        holding: {
            dividendes: h.dividendes,
            sansHolding: h.sans.net,
            avecHolding: h.avec.net,
            gain: h.gain,
            gainPrudent: h.gainPrudent,
        },
    };
}

/**
 * La trajectoire, année par année.
 *
 * @param chiffreAffaires le chiffre d'affaires de l'année en cours, hors taxes
 * @param croissance      la croissance annuelle, 0.2 pour 20 %
 * @param partDepenses    la part du chiffre d'affaires qui part en dépenses
 * @param besoin          ce que vous voulez vous verser par an pour vivre, net
 * @param anneeDepart     l'année en cours
 */
export function trajectoire({
    chiffreAffaires,
    croissance = 0,
    partDepenses = 0,
    activite = "services",
    enCouple = false,
    enfants = 0,
    besoin = 0,
    anneeDepart = 2026,
    annees = ANNEES,
}) {
    const plafond = PLAFONDS[activite];
    const tva = franchiseTva(activite);
    const lignes = [];
    let regime = null;

    for (let rang = 0; rang < annees; rang++) {
        const annee = anneeDepart + rang;
        const ca = positif(chiffreAffaires) * Math.pow(1 + positif(croissance), rang);
        const charges = ca * Math.min(1, positif(partDepenses));
        const r = comparerRegimes({ chiffreAffaires: ca, charges, activite, enCouple, enfants });
        const nets = Object.fromEntries(r.scenarios.map((s) => [s.regime, s.net]));

        // Le régime micro cesse l'année qui suit deux années de suite
        // au-dessus du plafond. Les années d'avant le calcul sont inconnues :
        // on les suppose sous le plafond, ce qui garde la micro ouverte les
        // deux premières années.
        const precedentes = lignes.slice(-2).map((l) => l.chiffreAffaires);
        const microFermee = precedentes.length === 2 && precedentes.every((c) => c > plafond);

        const ouverts = ORDRE_REGIMES.filter((reg) => !(reg === "micro" && microFermee));
        // Jamais en arrière : seuls restent le régime en cours et les suivants.
        const permis = regime === null ? ouverts : ouverts.filter((reg) => ORDRE_REGIMES.indexOf(reg) >= ORDRE_REGIMES.indexOf(regime));
        const meilleur = permis.reduce((a, b) => (nets[b] > nets[a] ? b : a));

        let choix;
        let raison;
        if (regime === null) {
            // La première année part de la micro, sauf si la quitter rapporte
            // déjà au moins mille euros.
            choix = nets[meilleur] - nets.micro >= ECART_NEGLIGEABLE ? meilleur : "micro";
            raison = choix === "micro" ? "depart" : "gain";
        } else if (!permis.includes(regime)) {
            choix = meilleur;
            raison = "plafond";
        } else if (nets[meilleur] - nets[regime] >= ECART_NEGLIGEABLE) {
            choix = meilleur;
            raison = "gain";
        } else {
            choix = regime;
            raison = "reste";
        }

        const changement = regime !== null && choix !== regime;
        const societe = SOCIETES.includes(choix)
            ? surplusSociete({ forme: choix, chiffreAffaires: ca, charges, besoin, activite })
            : null;

        lignes.push({
            annee,
            rang,
            chiffreAffaires: ca,
            charges,
            nets,
            meilleur,
            choix,
            raison,
            changement,
            // Ce que le changement rapporte l'année où il a lieu, par rapport
            // au régime qu'on quitte.
            gainChangement: changement ? nets[choix] - nets[regime] : 0,
            // Depuis la micro, pour situer le lecteur qui y est aujourd'hui.
            gainSurMicro: nets[choix] - nets.micro,
            microFermee,
            depassePlafond: ca > plafond,
            tva: ca > tva,
            societe,
        });
        regime = choix;
    }

    // Les moments à retenir, dans l'ordre où ils arrivent.
    const premierChangement = lignes.find((l) => l.changement || (l.rang === 0 && l.choix !== "micro")) || null;
    const fermetureMicro = lignes.find((l) => l.microFermee) || null;
    const entreeTva = lignes.find((l) => l.tva) || null;
    const holding =
        lignes.find((l) => l.societe?.holding && l.societe.holding.gainPrudent >= ECART_NEGLIGEABLE) || null;

    return {
        lignes,
        plafond,
        franchiseTva: tva,
        premierChangement,
        fermetureMicro,
        entreeTva,
        holding,
        regimeFinal: lignes[lignes.length - 1].choix,
        // La micro de bout en bout : aucun changement ne vaut ses frais.
        toujoursMicro: lignes.every((l) => l.choix === "micro"),
    };
}
